import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Input, Select, message, Radio } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { saveOrUpdateDocument } from 'api/documentApi';
import { TAG_OPTIONS } from 'pages/dashboard/ProspectsDetailPage/components/DocumentTab/components/UploadDocumentModal';
import s from './s.module.less';

interface SaveDocumentModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    fileId: number;
    imageUrl: string;
    fileName?: string;
    clientId: number;
}

const SaveDocumentModal: React.FC<SaveDocumentModalProps> = ({
    open,
    onClose,
    onSuccess,
    fileId,
    imageUrl,
    fileName,
    clientId,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            form.resetFields();
            // 初始化表单值
            form.setFieldsValue({
                name: fileName || '',
                tag: [],
                visibleToProvider: true,
                noteToProvider: '',
            });
        }
    }, [open, form, fileName]);

    const handleOk = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            const res = await saveOrUpdateDocument([{
                name: values.name,
                tag: values.tag || [],
                fileUploadId: fileId,
                note: values.noteToProvider || '',
                visibleSubjectIds: values.visibleToProvider ? [clientId] : [],
                source: 'MESSAGE',
            }], clientId.toString());
            if (!res.error) {
                message.success('Document saved successfully');
                onSuccess?.();
                onClose();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            footer={[
                <Button key="cancel" onClick={handleClose} className={s.cancelButton}>Cancel</Button>,
                <Button key="save" type="primary" onClick={handleOk} className={s.saveButton} loading={loading}>Save</Button>,
            ]}
            width={640}
            className={s.modal}
            closeIcon={<CloseOutlined className={s.closeIcon} />}
            title='Save to "Documents"'
            bodyStyle={{
                maxHeight: 'calc(90vh - 110px)',
                overflowY: 'auto',
                padding: '24px',
            }}
        >
            <div className={s.modalContent}>
                {/* 文档预览区域 */}
                <img
                    src={imageUrl}
                    alt="Document preview"
                    className={s.previewImage}
                />

                {/* 表单区域 */}
                <Form form={form} layout="vertical" className={s.form}>
                    <Form.Item
                        label="Name"
                        name="name"
                        rules={[{ required: true, message: 'Please enter the document name' }]}
                        className={s.formItem}
                    >
                        <Input placeholder="{File name}" />
                    </Form.Item>

                    <Form.Item
                        name="tag"
                        label="Tag"
                        className={s.formItem}
                    >
                        <Select
                            placeholder="Select or enter tags"
                            className={s.tagSelect}
                            mode="tags"
                            allowClear
                            maxTagCount={3}
                            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                            tokenSeparators={[',']}
                        >
                            {TAG_OPTIONS.map((tag) => (
                                <Select.Option key={tag} value={tag}>
                                    {tag}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="visibleToProvider"
                        label="Visible to patient?"
                        className={s.formItem}
                        initialValue={true}
                    >
                        <Radio.Group className={s.radioGroup}>
                            <Radio value={true}>Yes</Radio>
                            <Radio value={false}>No</Radio>
                        </Radio.Group>
                    </Form.Item>


                    <Form.Item
                        label="Note to provider"
                        name="noteToProvider"
                        className={s.formItem}
                    >
                        <div>
                            <div className={s.noteDescription}>
                                Optional message to let the provider know what this document is for
                            </div>
                            <Input
                                placeholder="Optional message to let the provider know what this document is for"
                            />
                        </div>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

export default SaveDocumentModal;
